<template>
  <div class="cognito-login">
    <Authenticator :hide-sign-up="true">
      <template v-slot:sign-in-header>
        <div style="padding: var(--amplify-space-large); text-align: center">
          <img
            class="oudirect-logo"
            alt="OUDirect logo"
            src="https://oudesktop.s3.us-east-1.amazonaws.com/oudirect_logo.png"
          />
        </div>
        <h3 class="cognito-login__heading sign-in">Sign in</h3>
      </template>
      <template v-slot:forgot-password-header>
        <div style="padding: var(--amplify-space-large); text-align: center">
          <img
            class="oudirect-logo"
            alt="OUDirect logo"
            src="https://oudesktop.s3.us-east-1.amazonaws.com/oudirect_logo.png"
          />
        </div>
        <h3 class="cognito-login__heading">Reset Password</h3>
      </template>
      <template v-slot:confirm-reset-password-header>
        <div style="padding: var(--amplify-space-large); text-align: center">
          <img
            class="oudirect-logo"
            alt="OUDirect logo"
            src="https://oudesktop.s3.us-east-1.amazonaws.com/oudirect_logo.png"
          />
        </div>
        <h3 class="cognito-login__heading">Reset Password</h3>
      </template>
      <template v-slot:sign-in-footer>
        <div class="sign-in-footer" data-amplify-footer="">
          <div class="amplify-flex amplify-authenticator__federated-buttons">
            <hr
              class="amplify-divider amplify-divider--horizontal amplify-divider--small"
              aria-orientation="horizontal"
              data-label="or"
            />
            <button
              class="amplify-button amplify-field-group__control amplify-authenticator__federated-button"
              type="button"
              data-fullwidth="false"
              data-loading="false"
              data-disabled="false"
              data-amplify-button=""
              @click="oktaSignIn"
            >
              <OktaOidcIcon />
              <span class="amplify-text"
                >Sign In with OU Employee Domain Account</span
              >
            </button>
          </div>
          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>

          <button
            class="amplify-button amplify-field-group__control amplify-button--link amplify-button--small amplify-field-group__control amplify-authenticator__font"
            type="button"
            data-fullwidth="false"
            data-size="small"
            data-variation="link"
            data-loading="false"
            data-disabled="false"
            fullwidth="true"
            data-amplify-button=""
            style="font-weight: normal"
            @click="auth.toForgotPassword()"
          >
            Forgot your password?
          </button>
          <div class="assistance-message">
            If you require any assistance accessing your account, please contact
            <a href="mailto:DirectHelp@ou.org">DirectHelp@ou.org</a>
          </div>
        </div>
      </template>
    </Authenticator>
  </div>
</template>

<script setup lang="ts">
import cognitoAuth from "@/shared/access/CognitoAuth";
import { SessionCacheService } from "@/shared/services/SessionCacheService";
import { CacheService } from "@/shared/services/CacheService";
import { useMainStore } from "@/stores/useMainStore";
import { onMounted, ref, watchEffect } from "vue";
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-vue";
import "@aws-amplify/ui-vue/styles.css";
import "@/assets/cognito-overrides.scss";
import OktaOidcIcon from "@/icons/OktaOidcIcon.vue";

const mainStore = useMainStore();
const auth = useAuthenticator();

const errorMessage = ref("");

const clearCache = function () {
  // Clear any existing session cache
  SessionCacheService.clearSessionCache();
  CacheService.removeAllExpired();
  mainStore.resetPersistedState();
};

const oktaSignIn = async function () {
  try {
    errorMessage.value = "";

    // Initiate hosted UI sign in
    await cognitoAuth.signInWithOkta();
  } catch (error: any) {
    console.error("Cognito sign in error:", error);
    errorMessage.value = "Failed to initiate sign in. Please try again.";
  }
};

// Once user is authenticated, redirect to original URI or home page
watchEffect(() => {
  if (auth.route === "authenticated") {
    const originalUri = cognitoAuth.getOriginalUri() || "/";
    cognitoAuth.removeOriginalUri();
    window.location.href = originalUri;
  }
});

onMounted(async () => {
  // If already authenticated, redirect to original URI or home page
  await cognitoAuth.initializeAsync();
  if (await cognitoAuth.isAuthenticated()) {
    const originalUri = cognitoAuth.getOriginalUri() || "/";
    cognitoAuth.removeOriginalUri();
    window.location.href = originalUri;
    return;
  }
  clearCache();
});
</script>

<style lang="scss">
.cognito-login {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f5f5f5;

  &__heading {
    font-size: 1.5rem;
    font-weight: 600;

    &.sign-in {
      padding: 0 var(--amplify-space-xl);
    }
  }

  [data-amplify-authenticator-signin] {
    padding-bottom: 8px;
    padding-top: 0;
  }

  .sign-in-footer {
    padding: var(--amplify-components-authenticator-form-padding);
    padding-top: 0;
  }

  .assistance-message {
    margin-top: 16px;

    a {
      color: #006ce0;
    }
  }
}
</style>
